import { TRPCClientError } from "@trpc/client";
import { trpcClient } from "./trpc";

const STORAGE_KEY = "pluck_pending_ref";

/** Keep an invite code until the visitor is signed in (survives OAuth round-trips). */
export function rememberReferral(code: string): void {
	try {
		localStorage.setItem(STORAGE_KEY, code);
	} catch {
		// Storage unavailable (private mode); attribution is best-effort.
	}
}

function readPending(): string | null {
	try {
		return localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

function clearPending(): void {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}

/** Codes the server rejects permanently; retrying would never succeed. */
const PERMANENT_ERRORS = new Set(["NOT_FOUND", "BAD_REQUEST"]);

/**
 * Attribute a remembered invite code to the signed-in user. Safe to call on
 * every app load: it's a no-op without a pending code, and clears the code
 * once the server accepted or permanently rejected it.
 */
export async function consumePendingReferral(): Promise<boolean> {
	const code = readPending();
	if (!code) {
		return false;
	}
	try {
		await trpcClient.referral.attributeReferral.mutate({ code });
		clearPending();
		return true;
	} catch (err) {
		if (
			err instanceof TRPCClientError &&
			PERMANENT_ERRORS.has(String(err.data?.code))
		) {
			clearPending();
		}
		return false;
	}
}
